import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import classnames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import { Row, Col, Typography, Button, Space, Modal } from 'antd';

import {
  Form,
  FormButton,
  dialogFunction,
  CustomNotification,
  FormCancelButton,
  FormEditButton,
} from 'common/components';
import ModalUpdateDocumentOrCertification from './ModalUpdateDocsAndCertificates';
import PreviewablePdfModal from 'common/components/previewable-modal/PreviewablePdfModal';
import PreviewableModal from 'common/components/previewable-modal/PreviewableModal';
import AddAssetPimModal from 'pages/product-full-view/components/modal/add-asset-pim-modal';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import * as qaSelectors from 'pages/qa-spec/controllers/selectors';

import {
  useSetFormUpdateDocsAndCertification,
  useArtworkDocuments,
  useQaSpecTemplateDocuments,
} from './hooks';
import { useCheckQaSpecLocked } from 'pages/qa-spec/hooks/useCheckQaSpecLocked';
import { useCheckAllowEdit } from 'pages/qa-spec/hooks';
import { useTabExpand } from 'hooks/useTabExpandProduct';
import { useReloadDocumentCertificateTab } from 'hooks/useReloadDocumentCertificateTab';

import { bytesToMegaBytes } from 'utils/string';
import { formatMDY } from 'utils/formatDate';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';

import { getQaContainTabDataQueryKey } from 'pages/qa-spec/components/qa-product-contain/queries';

import * as api from 'config/axios';
import * as services from 'services/product';
import * as servicesQaSpec from 'services/qaSpec';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';

import { QaGridEdit } from 'pages/qa-spec/components';

import { getCellRenderer, fetchEgnyteFileBlob } from './utils';
import { checkExpiryDateForCertification } from 'utils/checkExpiryDateForCertification';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { useGetProductFullView } from 'hooks';
import UploadCertificate from '../qa-prod-spec/UploadCertificate';

import {
  useQueryQaSpecCertificateSnapshotData,
  useQueryQaSpecDocumentSnapshotData,
} from './queries';
import QaSpecDocuments from '../qa-spec-documents/QaSpecDocuments';

import {
  useCheckDisableDownloadAsset,
  useCheckPrivateLabel,
} from 'hooks/useCheckDisableDownload';
import { useGetQaSpecCertificates } from '../qa-contact/queries';

import { TOOlTIP_FIELDS } from 'static/tooltipFields';

import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-alpine.css';
import './QaSpecDocsAndCertificates.less';
import { COMMENT_FIELDS } from 'static/QaSpecFields';
import { API_ENDPOINT } from 'config/env';
import { forwardTo, forwardToAndReplace } from 'utils/common/route';

const QaSpecDocsAndCertificates = ({ productId }) => {
  const intl = useIntl();
  const docGridRef = useRef(null);
  const cerGridRef = useRef(null);
  const jobDocGridRef = useRef(null);
  const qaSpecTemplateDocGridRef = useRef(null);
  const [rowDataJobDocs, setRowDataJobDocs] = useState([]);
  const [rowDataQaSpecTemplateDocs, setRowDataQaSpecTemplateDocs] = useState(
    [],
  );
  const queryClient = useQueryClient();

  const reloadDocumentAndCertificateTab = useSelector(
    qaSelectors.selectReloadDocumentAndCertificate(),
  );

  const [rowDataDoc, setRowDataDoc] = useState([]);
  const [visible, setVisible] = useState(false);
  const [pdfModalData, setPdfModalData] = useState({
    visible: false,
    link: '',
    fileName: '',
    fileType: '',
    extensionFile: '',
    isDocument: true,
    fileSize: 0,
    mediaType: 'Document',
  });
  const [openDocument, setOpenDocument] = useState(false);
  const [openCertificate, setOpenCertificate] = useState(false);

  const [isEdit, setIsEdit] = useState(false);

  const [rowPreview, setRowPreview] = useState();

  const [formInstance] = Form.useForm();

  const [selectedRow, setSelectedRow] = useState({
    document: null,
    certificate: null,
  });

  const [visibleModalUpdate, setVisibleModalUpdate] = useState({
    type: null,
    value: false,
  });

  const useSnapshot = useCheckSnapshotForRetailer(productId);
  const { data: snapshotDocsData } = useQueryQaSpecDocumentSnapshotData({
    productId,
    enabled: Boolean(productId && useSnapshot),
  });

  const { data: snapshotCertsData } = useQueryQaSpecCertificateSnapshotData({
    productId,
    enabled: Boolean(productId && useSnapshot),
  });

  const { productFull } = useGetProductFullView({
    productId,
  });

  const snapshotDocsGridData = snapshotDocsData?.documents;
  const snapshotCertsGridData = snapshotCertsData?.certificates;

  const { tableStyle } = useTabExpand();

  const { toggleReloadDocumentWhenDelete } = useReloadDocumentCertificateTab();

  const { data: artworkDocData, isLoading: isJobDocsLoading } =
    useArtworkDocuments(productId);

  const {
    data: qaSpecTemplateDocData,
    isLoading: isQaSpecTemplateDocsLoading,
  } = useQaSpecTemplateDocuments(productId);

  const egnyteToken = artworkDocData?.egnyteToken;
  const egnyteEndPoint = artworkDocData?.egnyteEndPoint;
  const jobDocs = artworkDocData?.artworkDocuments || [];

  useSetFormUpdateDocsAndCertification({
    isEnabled: Boolean(visibleModalUpdate.value),
    type: visibleModalUpdate.type,
    formInstance,
    dataForm:
      visibleModalUpdate.type === 'document'
        ? selectedRow.document
        : selectedRow.certificate,
  });

  const { isQASpecLocked } = useCheckQaSpecLocked();

  const fileType = rowPreview?.fileExtension || rowPreview?.fileType;

  const pdfPreview =
    rowPreview?.pdfPreview ||
    rowPreview?.thumbSizeFull ||
    rowPreview?.thumbSize2500 ||
    rowPreview?.thumbSize1000 ||
    rowPreview?.thumbSize500;

  const hasPdfPreview = Boolean(pdfPreview);
  const { linkPreview, extensionFile } = hasPdfPreview
    ? checkShowPdfPreview(
        pdfPreview,
        rowPreview?.linkUrl || rowPreview?.url,
        fileType,
      )
    : {
        linkPreview: rowPreview?.linkUrl || rowPreview?.url,
        extensionFile: fileType,
      };

  const invalidateData = () => {
    //* reload product contain tab
    queryClient.invalidateQueries({
      queryKey: getQaContainTabDataQueryKey(productId),
    });
  };

  const handleDelete = useCallback((linkId, isDocumentType) => {
    services
      .qaSpecUnLinkToDocCer({ linkId: linkId })
      .then((response) => {
        const name = isDocumentType ? 'Document' : 'Certificate';
        if (response?.isSuccess) {
          CustomNotification.success(
            intl.formatMessage(Messages.qaSpecDocsAndCerRemoveMessageSuccess, {
              name,
            }),
          );
          if (isDocumentType) {
            getQaSpecDoc();
            clearSelectedRow('document');
            toggleReloadDocumentWhenDelete();
            return;
          }

          clearSelectedRow('certificate');
          //* reload product contain tab
          invalidateData();
        } else {
          CustomNotification.error(response?.message);
        }
      })
      .catch((e) => {})
      .finally(() => {});
  }, []);

  const handleDeleteModal = useCallback(
    (linkId, isDocumentType) => {
      dialogFunction({
        type: 'warn',
        content: 'Are you sure you want to delete the selected item?',
        okText: 'Delete',
        okButtonProps: {
          type: 'danger',
        },
        onOk: () => handleDelete(linkId, isDocumentType),
      });
    },
    [handleDelete],
  );

  const handleQaSpecDelete = useCallback((linkId) => {
    services
      .qaSpecUnLinkExcelDoc({ linkId: linkId })
      .then((response) => {
        const name = 'Document';
        if (response?.isSuccess) {
          CustomNotification.success(
            intl.formatMessage(Messages.qaSpecDocsAndCerRemoveMessageSuccess, {
              name,
            }),
          );
          setRowDataQaSpecTemplateDocs((prevData) =>
            prevData.filter((doc) => doc.linkId !== linkId),
          );
          invalidateData();
        } else {
          CustomNotification.error(response?.message);
        }
      })
      .catch((e) => {})
      .finally(() => {});
  }, []);

  const handleQaSpecDeleteModal = useCallback(
    (linkId) => {
      dialogFunction({
        type: 'warn',
        content: 'Are you sure you want to delete the selected item?',
        okText: 'Delete',
        okButtonProps: {
          type: 'danger',
        },
        onOk: () => handleQaSpecDelete(linkId),
      });
    },
    [handleQaSpecDelete],
  );

  const { checkDisableDownloadAssetDetail } = useCheckDisableDownloadAsset();

  const { isProductFullView } = useCheckPrivateLabel();

  const CellRenderAction = useCallback(
    (params) => {
      const isDocumentType = params?.data.hasOwnProperty('documentName');

      const disableDownloadDocument = checkDisableDownloadAssetDetail(
        params?.data,
      );

      const disabled = isDocumentType
        ? disableDownloadDocument
        : !isProductFullView;

      return (
        <>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => {
              setRowPreview(params?.data);
              setVisible(true);
            }}
          />
          <Button
            type='link'
            icon={<DownloadOutlined />}
            disabled={disabled}
            onClick={() => {
              const downloadUrl = isDocumentType
                ? params?.data?.linkUrl
                : params?.data?.url;

              if (!downloadUrl) {
                CustomNotification.error('Could not download file!');
              }
              api.sendDownload({
                url: downloadUrl,
              });
            }}
          />
          {!isQASpecLocked && isDocumentType ? (
            <Button
              type='link'
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                handleDeleteModal(params?.data?.linkId, isDocumentType);
              }}
            />
          ) : null}
        </>
      );
    },
    [isQASpecLocked, handleDeleteModal],
  );

  const columnDefsDoc = useMemo(() => {
    const cellRenderer = getCellRenderer({
      snapshotGridValues: snapshotDocsGridData,
      useSnapshot,
      CellRenderAction,
    });

    const cols = [
      {
        dataType: 'string',
        displayName: 'Document Name',
        field: 'documentName',
        fieldNameCamelCase: 'documentName',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.documentName.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      // {
      //   dataType: 'string',
      //   displayName: 'Document Type',
      //   fieldName: 'documentType',
      //   fieldNameCamelCase: 'documentType',
      // },
      {
        dataType: 'string',
        displayName: 'Type',
        field: 'type',
        fieldNameCamelCase: 'type',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateType.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Sub Type',
        field: 'subtype',
        fieldNameCamelCase: 'subtype',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateSubType.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'File Type',
        field: 'fileType',
        fieldNameCamelCase: 'fileType',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateFileType.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'File Size',
        field: 'fileSize',
        fieldNameCamelCase: 'fileSize',
        valueGetter: (params) => {
          return `${bytesToMegaBytes(params?.data.fileSize)} MB`;
        },
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateFileSize.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'DateTime',
        displayName: 'Upload Date',
        field: 'uploadDate',
        fieldName: 'uploadData',
        fieldNameCamelCase: 'uploadDate',
        valueGetter: ({ data }) => formatMDY(data?.['uploadDate']),
        cellRenderer: cellRenderer['TextRenderForDate'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateUploadDate.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        displayName: '',
        field: 'actions',
        cellRenderer: cellRenderer['CellRenderAction'],
        cellRendererParams: {
          isDocumentType: true,
        },
      },
    ];

    return cols.map((fieldItem) => {
      const nullCellEditor = () => {};

      return {
        cellEditorSelector: nullCellEditor,
        ...fieldItem,
        minWidth: 100,
        width: 200,
        flex: fieldItem?.field === 'actions' ? 0 : 1,
        headerComponent:
          fieldItem?.field === 'actions' ? null : QaSpecHelpContentGridLabel,
      };
    });
  }, [useSnapshot, CellRenderAction, JSON.stringify(snapshotDocsGridData)]);

  const columnDefsCer = useMemo(() => {
    const cellRenderer = getCellRenderer({
      snapshotGridValues: snapshotCertsGridData,
      useSnapshot,
      CellRenderAction,
    });

    const cols = [
      {
        dataType: 'string',
        displayName: 'Certificate Level',
        field: 'level',
        fieldNameCamelCase: 'level',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateLevel.fieldPath,
        },
      },
      {
        dataType: 'string',
        displayName: 'Certificate',
        field: 'certificateName',
        fieldNameCamelCase: 'certificateName',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateName.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Certificate Type',
        field: 'type',
        fieldNameCamelCase: 'type',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateType.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Certificate Body',
        field: 'certificateBody',
        fieldNameCamelCase: 'certificateBody',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateBody.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Status',
        field: 'status',
        fieldNameCamelCase: 'status',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateStatus.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        dataType: 'DateTime',
        displayName: 'Certificate Expiry Date',
        field: 'expirationDate',
        fieldNameCamelCase: 'expirationDate',
        valueGetter: ({ data }) => formatMDY(data?.['expirationDate']),
        cellRenderer: cellRenderer['TextRenderForDate'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.certificateExpiryDate.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificate.qaSpecName,
        },
      },
      {
        displayName: '',
        field: 'actions',
        cellRenderer: cellRenderer['CellRenderAction'],
      },
    ];

    return cols.map((fieldItem) => {
      const nullCellEditor = () => {};

      return {
        cellEditorSelector: nullCellEditor,
        ...fieldItem,
        minWidth: 100,
        width: 200,
        flex: fieldItem?.field === 'actions' ? 0 : 1,
        headerComponent:
          fieldItem?.field === 'actions' ? null : QaSpecHelpContentGridLabel,
      };
    });
  }, [useSnapshot, JSON.stringify(snapshotCertsGridData)]);

  const columnDefsJobDocs = useMemo(() => {
    const cellRenderer = getCellRenderer({
      snapshotGridValues: [],
      useSnapshot: false,
      CellRenderAction,
    });

    const cols = [
      {
        dataType: 'string',
        displayName: 'Job Id',
        field: 'jobNumber',
        fieldNameCamelCase: 'jobNumber',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.finalArtJobId.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificateFinalArt.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'SKU Name',
        field: 'skuName',
        fieldNameCamelCase: 'skuName',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.finalArtSKUName.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificateFinalArt.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Document Name',
        field: 'documentName',
        fieldNameCamelCase: 'documentName',
        cellRenderer: cellRenderer['TextRender'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.finalArtDocumentName.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificateFinalArt.qaSpecName,
        },
      },
      {
        dataType: 'DateTime',
        displayName: 'Upload Date',
        field: 'artwork_Last_modified',
        fieldNameCamelCase: 'artwork_Last_modified',
        valueGetter: ({ data }) => formatMDY(data.artwork_Last_modified),
        cellRenderer: cellRenderer['TextRenderForDate'],
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.finalArtUploadDate.fieldPath,
          qaSpecName: COMMENT_FIELDS.documentAndCertificateFinalArt.qaSpecName,
        },
      },
      {
        displayName: '',
        field: 'actions',
        cellRenderer: (params) => {
          const token = egnyteToken;
          const baseUrl = egnyteEndPoint;
          const fileId = params.data.group_Id;
          const fileName = params.data.documentName;

          const previewDoc = async () => {
            try {
              const { blob } = await fetchEgnyteFileBlob(
                fileId,
                token,
                baseUrl,
              );
              const newBlobUrl = URL.createObjectURL(blob);

              if (pdfModalData.link) {
                URL.revokeObjectURL(pdfModalData.link);
              }

              setPdfModalData({
                visible: false,
              });

              setTimeout(() => {
                setPdfModalData({
                  visible: true,
                  link: newBlobUrl,
                  fileName,
                  fileType: 'application/pdf',
                  extensionFile: 'pdf',
                  isDocument: true,
                  fileSize: blob.size,
                  mediaType: 'Document',
                });
              }, 0);
            } catch (error) {
              CustomNotification.error('Preview failed!');
            }
          };

          const downloadDoc = async () => {
            try {
              const { blob } = await api.fetchEgnyteFileWithProgress(
                fileId,
                fileName,
                token,
                baseUrl,
              );
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = fileName;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (error) {
              CustomNotification.error('Download failed!');
            }
          };

          return (
            <>
              <Button type='link' icon={<EyeOutlined />} onClick={previewDoc} />
              <Button
                type='link'
                icon={<DownloadOutlined />}
                onClick={downloadDoc}
              />
            </>
          );
        },
      },
    ];

    return cols.map((fieldItem) => ({
      cellEditorSelector: () => {},
      ...fieldItem,
      minWidth: 100,
      width: 200,
      flex: fieldItem.field === 'actions' ? 0 : 1,
      headerComponent:
        fieldItem.field === 'actions' ? null : QaSpecHelpContentGridLabel,
    }));
  }, [
    bytesToMegaBytes,
    formatMDY,
    CellRenderAction,
    checkDisableDownloadAssetDetail,
    egnyteToken,
    egnyteEndPoint,
  ]);

  // qa spec template columns
  const columnDefsQaSpecTemplateDocs = useMemo(() => {
    const cols = [
      {
        dataType: 'string',
        displayName: 'Document Name',
        field: 'documentName',
        fieldNameCamelCase: 'documentName',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateDocumentName.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'Type',
        field: 'assetType',
        fieldNameCamelCase: 'assetType',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateAssetType.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'File Type',
        field: 'fileExtension',
        fieldNameCamelCase: 'fileExtension',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateFileExtension.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
      },
      {
        dataType: 'string',
        displayName: 'File Size',
        field: 'fileSize',
        fieldNameCamelCase: 'fileSize',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateFileSize.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
        valueGetter: ({ data }) => `${bytesToMegaBytes(data?.fileSize)} MB`,
      },
      {
        dataType: 'DateTime',
        displayName: 'Download Date',
        field: 'downloadDate',
        fieldNameCamelCase: 'downloadDate',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateDownloadDate.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
        valueGetter: ({ data }) => formatMDY(data?.downloadDate),
      },
      {
        dataType: 'DateTime',
        displayName: 'Upload Date',
        field: 'uploadDate',
        fieldNameCamelCase: 'uploadDate',
        headerComponentParams: {
          fieldPath: TOOlTIP_FIELDS.qaSpecTemplateUploadDate.fieldPath,
          qaSpecName: COMMENT_FIELDS.qaSpecTemplateDocuments.qaSpecName,
        },
        valueGetter: ({ data }) => formatMDY(data?.uploadDate),
      },
      {
        displayName: '',
        field: 'actions',
        cellRenderer: (params) => {
          const previewDoc = async () => {
            setRowPreview(params?.data);
            setVisible(true);
          };

          const downloadDoc = async () => {
            try {
              const downloadUrl = params?.data?.linkUrl;

              if (!downloadUrl) {
                CustomNotification.error('Could not download file!');
              }
              api.sendDownload({
                url: downloadUrl,
              });
            } catch (error) {
              CustomNotification.error('Download failed!');
            }
          };

          return (
            <>
              {/* preview */}
              {/* <Button type='link' icon={<EyeOutlined />} onClick={previewDoc} /> */}

              {/* download */}
              <Button
                type='link'
                icon={<DownloadOutlined />}
                onClick={downloadDoc}
              />

              {/* delete */}
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  handleQaSpecDeleteModal(params?.data?.linkId);
                }}
              />
            </>
          );
        },
      },
    ];

    return cols.map((fieldItem) => ({
      cellEditorSelector: () => {},
      ...fieldItem,
      minWidth: 100,
      width: 200,
      flex: fieldItem.field === 'actions' ? 0 : 1,
      headerComponent:
        fieldItem.field === 'actions' ? null : QaSpecHelpContentGridLabel,
    }));
  }, [
    bytesToMegaBytes,
    formatMDY,
    egnyteToken,
    egnyteEndPoint,
    pdfModalData.link,
  ]);

  const getQaSpecDoc = useCallback(() => {
    services
      .getQaSpeecDataDocument({ productId })
      .then((response) => {
        if (response?.isSuccess && response?.data?.documents)
          setRowDataDoc(response?.data?.documents);
      })
      .catch((e) => {})
      .finally(() => {});
  }, [productId]);

  const { certificateList } = useGetQaSpecCertificates({
    id: productId,
    isEnabled: true,
  });

  const fetchQaSpecDocAndCer = () => {
    setSelectedRow({
      document: null,
      certificate: null,
    });
    getQaSpecDoc();
  };

  useEffect(() => {
    fetchQaSpecDocAndCer();
  }, []);

  useEffect(() => {
    setRowDataJobDocs(artworkDocData?.artworkDocuments || []);
  }, [artworkDocData?.artworkDocuments]);

  useEffect(() => {
    setRowDataQaSpecTemplateDocs(
      qaSpecTemplateDocData?.templateDocuments || [],
    );
  }, [qaSpecTemplateDocData?.templateDocuments]);

  useEffect(() => {
    if (reloadDocumentAndCertificateTab) {
      fetchQaSpecDocAndCer();
    }
  }, [reloadDocumentAndCertificateTab]);

  const clearSelectedRow = (grid) => {
    setSelectedRow((prevState) => ({ ...prevState, [grid]: null }));

    const gridInstance =
      grid === 'document' ? docGridRef?.current : cerGridRef?.current;

    gridInstance.api.deselectAll();
  };

  const reloadGrid = () => {
    clearSelectedRow('document');
    getQaSpecDoc();
    setOpenDocument(false);
  };

  const handleNewDoc = () => {
    setOpenDocument(true);
  };

  const toggleEditMode = useCallback(() => {
    setIsEdit((prev) => !prev);
  }, []);

  const onCancel = () => {
    toggleEditMode();
    docGridRef?.current?.api.deselectAll();
    setSelectedRow((prevRow) => ({
      ...prevRow,
      document: null,
    }));
  };

  const onCancelModalUpdate = () => {
    setVisibleModalUpdate({
      type: '',
      value: false,
    });
  };

  const handleUpdateDoc = async () => {
    const values = await formInstance.validateFields();

    const params = {
      linkId: selectedRow?.document?.linkId,
      documentName: values.documentName,
      subtype: values?.subtype,
      documentType: selectedRow?.document?.documentType,
      type: selectedRow?.document?.type,
    };

    const response = await servicesQaSpec.updateDocumentOrCertification(params);

    if (response?.isSuccess) {
      CustomNotification.success(
        intl.formatMessage(Messages.qaSpecDocsAndCerUpdateMessageSuccess, {
          name: 'Document',
        }),
      );
      onCancelModalUpdate();
      getQaSpecDoc();
      clearSelectedRow('document');
      toggleReloadDocumentWhenDelete();
    } else {
      CustomNotification.error(response?.message ?? ' Something went wrong');
    }
  };

  const onSubmit = handleUpdateDoc;

  const onSuccessCallback = () => {
    clearSelectedRow('certificate');
  };

  const hasPermissionEdit = useCheckAllowEdit(productId);

  // const dispatch = useDispatch();
  const onClickLink = () => {
    Modal.confirm({
      title: 'Confirmation',
      content: (
        <>
          <p>
            <strong>Please save your changes before proceeding</strong>. You are being redirected to the{" "}
            <strong>Contacts, Locations, Certificates </strong> tab to add or edit details.
          </p>
          <p>
            <strong>After completing your changes, please refresh the page to see the latest updates.</strong>
          </p>
        </>
      ),
      okText: 'Continue',
      onOk: async () => {
        openTab({ key: 'contacts' });
      },
      width: 700
    });
  };

  const openTab = ({ key }) => {
    const uniqueParam = new Date().getTime(); // Add a unique timestamp
    forwardToAndReplace(`/product/qa-specification/${productId}?activeTab=${key}&_=${uniqueParam}`);
  };

  return (
    <Form style={{ paddingRight: 5 }} layout='vertical'>
      <Row style={{ flex: 1 }}>
        <Col xs={24}>
          <Row>
            <Space direction='vertical' style={{ width: '100%' }}>
              {/* Documents */}
              <div
                className='qa-spec-doc-n-cer__doc'
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Row align='bottom' style={{ width: '100%', height: 30 }}>
                    <Col span={12}>
                      <Typography.Title
                        level={5}
                        strong
                        style={{ marginBottom: 0 }}
                      >
                        Documents <span style={{ color: 'gray', fontWeight: 'normal' }}>(Not for Certificates)</span>
                      </Typography.Title>
                    </Col>
                    {!isQASpecLocked ? (
                      <>
                        {isEdit && (
                          <Col
                            span={12}
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Space>
                              <FormButton
                                text='New Document'
                                onClick={handleNewDoc}
                              />

                              <FormEditButton
                                text='Update Document'
                                disabled={!selectedRow?.document}
                                onClick={() =>
                                  setVisibleModalUpdate({
                                    value: true,
                                    type: 'document',
                                  })
                                }
                              />
                              <FormCancelButton onClick={onCancel} />
                            </Space>
                          </Col>
                        )}
                        {!isEdit && hasPermissionEdit && (
                          <Col
                            span={12}
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Button
                              className='qa-spec-doc-n-cer__edit-btn'
                              icon={<EditOutlined />}
                              onClick={toggleEditMode}
                              style={{ border: 'none' }}
                            />
                          </Col>
                        )}
                      </>
                    ) : null}
                  </Row>
                  <Row style={{ width: '100%' }}>
                    <Col span={24}>
                      <Row style={{ width: '100%' }}>
                        {columnDefsDoc && columnDefsDoc.length > 0 && (
                          <div
                            style={{
                              ...tableStyle,
                              width: '100%',
                            }}
                            className={classnames(
                              'ag-theme-alpine',
                              'ag-theme-alpine--with-scroller',
                            )}
                          >
                            <QaGridEdit
                              gridInst={docGridRef}
                              gridData={rowDataDoc}
                              overlayNoRowsTemplate='No Rows to Show'
                              rowSelection={'single'}
                              onRowSelected={(event) => {
                                if (!event?.node?.selected) return;

                                setSelectedRow((prevRow) => ({
                                  ...prevRow,
                                  document: event.data,
                                }));
                              }}
                              fields={columnDefsDoc}
                              snapshotGridValues={snapshotDocsGridData}
                              defaultColDef={{ resizable: true }}
                              isEdit={isEdit}
                            />
                          </div>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Space>
              </div>

              {/* Certificates */}
              <div
                className='qa-spec-doc-n-cer__cer'
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Row align='bottom' style={{ width: '100%', height: 30 }}>
                    <Col span={12}>
                      <Typography.Title
                        level={5}
                        strong
                        style={{ marginBottom: 0 }}
                      >
                        Certificates
                      </Typography.Title>
                    </Col>
                    <Col
                      span={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        className='qa-spec-doc-n-cer__edit-btn'
                        icon={<EditOutlined />}
                        onClick={onClickLink}
                        style={{ border: 'none' }}
                      />
                    </Col>
                  </Row>
                  <Row style={{ width: '100%' }}>
                    <Col span={24}>
                      <Row style={{ width: '100%' }}>
                        {columnDefsCer && columnDefsCer.length > 0 && (
                          <div
                            style={{
                              ...tableStyle,
                              width: '100%',
                            }}
                            className={classnames(
                              'ag-theme-alpine',
                              'ag-theme-alpine--with-scroller',
                            )}
                          >
                            <QaGridEdit
                              gridInst={cerGridRef}
                              gridData={certificateList}
                              overlayNoRowsTemplate='No Rows to Show'
                              rowSelection={'single'}
                              onRowSelected={(event) => {
                                if (!event?.node?.selected) return;

                                setSelectedRow((prevRow) => ({
                                  ...prevRow,
                                  certificate: event.data,
                                }));
                              }}
                              fields={columnDefsCer}
                              defaultColDef={{ resizable: true }}
                              isEdit={isEdit}
                              snapshotGridValues={snapshotCertsGridData}
                            />
                          </div>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Space>
              </div>

              {/* Fine Art */}
              <div
                className='qa-spec-doc-n-cer__job-doc'
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: 24,
                }}
              >
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Row align='bottom' style={{ width: '100%', height: 30 }}>
                    <Col span={12}>
                      <Typography.Title
                        level={5}
                        strong
                        style={{ marginBottom: 0 }}
                      >
                        Final Art
                      </Typography.Title>
                    </Col>
                  </Row>

                  <Row style={{ width: '100%' }}>
                    <Col span={24}>
                      <Row style={{ width: '100%' }}>
                        {columnDefsJobDocs && columnDefsJobDocs.length > 0 && (
                          <div
                            style={{
                              ...tableStyle,
                              width: '100%',
                            }}
                            className={classnames(
                              'ag-theme-alpine',
                              'ag-theme-alpine--with-scroller',
                            )}
                          >
                            <QaGridEdit
                              gridInst={jobDocGridRef}
                              gridData={rowDataJobDocs}
                              overlayNoRowsTemplate='No Rows to Show'
                              rowSelection={'single'}
                              onRowSelected={(event) => {
                                if (!event.node.selected) return;
                                setSelectedRow((prevRow) => ({
                                  ...prevRow,
                                  jobDoc: event.data,
                                }));
                              }}
                              fields={columnDefsJobDocs}
                              defaultColDef={{ resizable: true }}
                              isEdit={false}
                            />
                          </div>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Space>
              </div>

              {/* QA Spec Documents */}
              <QaSpecDocuments
                tableStyle={tableStyle}
                columnDefsJobDocs={columnDefsQaSpecTemplateDocs}
                rowDataQaSpecTemplateDocs={rowDataQaSpecTemplateDocs}
                qaSpecTemplateDocGridRef={qaSpecTemplateDocGridRef}
                onRowSelected={(event) => {
                  if (!event.node.selected) return;
                  setSelectedRow((prevRow) => ({
                    ...prevRow,
                    jobDoc: event.data,
                  }));
                }}
              />
            </Space>

            {openDocument && (
              <AddAssetPimModal
                onCloseModal={() => {
                  setOpenDocument(false);
                }}
                addType={'Document'}
                getImagery={[]}
                getDocument={reloadGrid}
                qaSpec={true}
              />
            )}

            {openCertificate && (
              <UploadCertificate
                visible={openCertificate}
                setVisible={setOpenCertificate}
                onSuccessCallback={onSuccessCallback}
                productId={productId}
                qaSpecName={COMMENT_FIELDS.documentAndCertificate.qaSpecName}
              />
            )}

            {visible && (
              <PreviewableModal
                id={rowPreview?.linkId}
                link={linkPreview}
                title={rowPreview?.certificateName || rowPreview?.documentName}
                visible={visible}
                fileName={
                  rowPreview?.certificateName || rowPreview?.documentName
                }
                cancelModal={() => setVisible(false)}
                downloadLink={rowPreview?.linkUrl || rowPreview?.url}
                isDownload={false}
                fileType={extensionFile || rowPreview?.fileExtension}
                fileSize={rowPreview?.fileSize}
                assetData={rowPreview}
                isDocument={true}
                hiddenTabs={['MasterReferenceLookup']}
              />
            )}
            <PreviewablePdfModal
              key={pdfModalData.link}
              title={'Final Art Document'}
              visible={pdfModalData.visible}
              cancelModal={() => {
                if (pdfModalData.link) {
                  URL.revokeObjectURL(pdfModalData.link);
                }
                setPdfModalData((prev) => ({ ...prev, visible: false }));
              }}
              fileName={pdfModalData.fileName}
              fileType={pdfModalData.extensionFile}
              link={pdfModalData.link}
              isDocument={pdfModalData.isDocument}
              fileSize={pdfModalData.fileSize}
            />
            {visibleModalUpdate.value && (
              <ModalUpdateDocumentOrCertification
                type={visibleModalUpdate.type}
                formInstance={formInstance}
                title={
                  visibleModalUpdate.type === 'document'
                    ? 'Update Document'
                    : 'Update Certificate'
                }
                visible={visibleModalUpdate.value}
                onCancel={onCancelModalUpdate}
                width={500}
                bodyStyle={{
                  minHeight: '150px',
                }}
                onOk={onSubmit}
                isRequiredExpiryDate={checkExpiryDateForCertification(
                  productFull?.ownerId,
                )}
              />
            )}
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

QaSpecDocsAndCertificates.propTypes = {
  productItemModules: PropTypes.object,
};
export default QaSpecDocsAndCertificates;
